import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';

type FAQItem = { question: string; answer: string; };
type FAQCategoryMeta = { id: 'general'|'account'|'trading'|'platform'|'deposits'|'security'; icon: string; count: number; };

export default function FAQSection() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<FAQCategoryMeta['id']>('general');
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const categories: FAQCategoryMeta[] = [
    { id: 'general',  icon: 'ri-information-line',   count: 4 },
    { id: 'account',  icon: 'ri-user-line',          count: 4 },
    { id: 'trading',  icon: 'ri-line-chart-line',    count: 4 },
    { id: 'platform', icon: 'ri-computer-line',      count: 4 },
    { id: 'deposits', icon: 'ri-bank-card-line',     count: 4 },
    { id: 'security', icon: 'ri-shield-check-line',  count: 4 },
  ];

  const activeData = useMemo(() => {
    const meta = categories.find(c => c.id === activeCategory)!;
    const items: FAQItem[] = Array.from({ length: meta.count }, (_, i) => ({
      question: t(`faq.${meta.id}.q${i + 1}`),
      answer:   t(`faq.${meta.id}.a${i + 1}`),
    }));
    return { ...meta, title: t(`faq.${meta.id}`), items };
  }, [activeCategory, t]);

  const toggleItem = (categoryId: string, index: number) => {
    const itemId = `${categoryId}-${index}`;
    const next = new Set(openItems);
    next.has(itemId) ? next.delete(itemId) : next.add(itemId);
    setOpenItems(next);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t('faq.tagline')}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('faq.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Category Navigation */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('faq.categories')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'border-orange-500 bg-orange-50 text-orange-600'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className={`${category.icon} text-2xl`}></i>
                  </div>
                  <div className="text-sm font-semibold">{t(`faq.${category.id}`)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Content */}
          {activeData && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <div className="w-10 h-10 flex items-center justify-center mr-4">
                  <i className={`${activeData.icon} text-2xl text-orange-600`}></i>
                </div>
                {activeData.title}
              </h3>

              <div className="space-y-4">
                {activeData.items.map((item, index) => {
                  const itemId = `${activeCategory}-${index}`;
                  const isOpen = openItems.has(itemId);
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleItem(activeCategory, index)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                      >
                        <span className="text-lg font-semibold text-gray-900 pr-4">
                          {item.question}
                        </span>
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-xl text-orange-600`}></i>
                        </div>
                      </button>

                      {isOpen && (
                        <div className="px-6 pb-4">
                          <div className="pt-2 border-t border-gray-100">
                            <p className="text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search Help */}
          <div className="mt-16 text-center bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('faq.cantFind')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('faq.supportText')}
            </p>
            <Button variant="primary" size="lg" className="whitespace-nowrap">
              {t('faq.contactSupport')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
